import React, { useCallback, useEffect, useState } from "react";
import { useAuthService } from "sensei-authentication";
import { useLoginContext } from "sensei-authentication/hooks";
import ITenantInfo from "../../models/TenantInfo";
import { getTenantInstallations } from "../../stores/tenantInstallationsStore";
import { updateCurrentUserPrefs } from "../../services/localPrefsService";
import TenantSelectionDialog from "./TenantSelection";

const SuccessfulAuth: React.FC = () => {
  const { authenticate, setIsBusy } = useAuthService();
  const { authenticatedAccount } = useLoginContext();

  const [tenants, setTenants] = useState<ITenantInfo[] | null | undefined>(undefined);

  const onTenantSelected = useCallback((tenant: ITenantInfo) => {
    updateCurrentUserPrefs({ tenantInstallationId: tenant.RecordId }, authenticatedAccount?.email);
    authenticate(tenant);
  }, [authenticate, authenticatedAccount]);

  useEffect(() => {
    setIsBusy(true);
    getTenantInstallations()
      .then(loadedTenants => {
        // If there's only one tenant, auto-select it without ever mounting the tenant
        // list UI -- authenticate() takes over the busy state.
        // Otherwise clear busy so the tenant list (or the no-access message) becomes visible.
        if (loadedTenants && loadedTenants.length === 1) {
          onTenantSelected(loadedTenants[0]);
        } else {
          setTenants(loadedTenants);
          setIsBusy(false);
        }
      })
      .catch(() => {
        setTenants(null);
        setIsBusy(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nothing is known yet (still fetching) or we're auto-selecting the sole tenant --
  // in both cases, render nothing rather than showing the selection card at all.
  if (tenants === undefined) {
    return null;
  }

  return (
    <TenantSelectionDialog
      tenants={tenants}
      onTenantSelected={onTenantSelected}
      currentUserEmail={authenticatedAccount?.email}
    />
  );
};

export default SuccessfulAuth;
